import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { OptimizedImage } from '../components/ui/OptimizedImage';

const CATALOG_URLS = {
    'aves-roedores': 'https://cdn.jaradpetfood.com/CATALOGO_JARAD_2025.pdf',
    'perros-gatos': 'https://cdn.jaradpetfood.com/JARAD-PERROS-Y-GATOS-2025.pdf',
};

const Catalog = () => {
    const handleOpenCatalog = (catalogType: keyof typeof CATALOG_URLS) => {
        window.open(CATALOG_URLS[catalogType], '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <Helmet>
                <title>Catálogos | Jarad - Nutrición Premium para Mascotas</title>
                <meta name="description" content="Descarga nuestros catálogos completos de productos Jarad para aves, roedores, perros y gatos. Encuentra toda la información sobre nuestra gama de nutrición premium." />
            </Helmet>

            <div className="min-h-screen" style={{ backgroundColor: '#f2ede4' }}>
                {/* Hero Section */}
                <div className="py-24">
                    <div className="content-container">
                        <motion.div
                            className="text-center max-w-4xl mx-auto"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight" style={{ color: '#0d0d0d' }}>
                                Nuestros Catálogos
                            </h1>
                            <p className="text-xl md:text-2xl font-light text-gray-600 mb-8 leading-relaxed">
                                Descarga nuestros catálogos completos y descubre toda la gama de productos Jarad
                                para el cuidado y nutrición de tus mascotas
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* Catalogs Section */}
                <div className="py-16">
                    <div className="content-container">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">

                            {/* Catálogo Aves y Roedores */}
                            <motion.div
                                className="relative rounded-3xl overflow-hidden shadow-xl hoverable-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <div className="relative h-[600px] md:h-[700px] overflow-hidden">
                                    <OptimizedImage 
                                        src="/images/catalogo-aves-roedores-00.jpg"
                                        alt="Catálogo Aves y Roedores"
                                        width={800}
                                        height={700}
                                        type="banner"
                                        className="absolute inset-0 w-full h-full object-cover"
                                        lazy={false}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                    <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                                        <div>
                                            <div className="inline-flex items-center gap-3 mb-4">
                                                <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#8ed1fc' }}></div>
                                                <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#f6cf02' }}></div>
                                            </div>
                                            <h2 className="text-2xl mb-2 font-light" style={{color: '#0d0d0d'}}>Catálogo</h2>
                                            <h3 className="text-3xl font-bold" style={{color: '#0d0d0d'}}>Aves y Roedores</h3>
                                        </div>

                                        <div>
                                            <p className="text-white/90 mb-6 leading-relaxed">
                                                Descubre nuestra completa gama de productos especializados para aves y roedores.
                                                Alimentación balanceada y complementos nutricionales de alta calidad.
                                            </p>
                                            <motion.button
                                                onClick={() => handleOpenCatalog('aves-roedores')}
                                                className="px-8 py-3 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg"
                                                style={{
                                                    backgroundColor: '#f6cf02',
                                                    color: '#0d0d0d',
                                                    borderColor: '#f6cf02'
                                                }}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#f6cf02';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f6cf02';
                                                    e.currentTarget.style.color = '#0d0d0d';
                                                }}
                                            >
                                                Ver Catálogo
                                            </motion.button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Catálogo Perros y Gatos */}
                            <motion.div
                                className="relative rounded-3xl overflow-hidden shadow-xl hoverable-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                <div className="relative h-[600px] md:h-[700px] overflow-hidden">
                                    <OptimizedImage 
                                        src="/images/catalogo-perros-gatos00.jpg"
                                        alt="Catálogo Perros y Gatos"
                                        width={800}
                                        height={700}
                                        type="banner"
                                        className="absolute inset-0 w-full h-full object-cover"
                                        lazy={false}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                    <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                                        <div>
                                            <div className="inline-flex items-center gap-3 mb-4">
                                                <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#e8b4a0' }}></div>
                                                <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#c7c7c7' }}></div>
                                            </div>
                                            <h2 className="text-2xl mb-2 font-light" style={{color: '#0d0d0d'}}>Catálogo</h2>
                                            <h3 className="text-3xl font-bold" style={{color: '#0d0d0d'}}>Perros y Gatos</h3>
                                        </div>

                                        <div>
                                            <p className="text-white/90 mb-6 leading-relaxed">
                                                Explora nuestra amplia selección de alimentos premium para perros y gatos.
                                                Nutrición especializada para cada etapa de vida y necesidades específicas.
                                            </p>
                                            <motion.button
                                                onClick={() => handleOpenCatalog('perros-gatos')}
                                                className="px-8 py-3 rounded-full font-medium transition-all border-2 shadow-md hover:shadow-lg"
                                                style={{
                                                    backgroundColor: '#f6cf02',
                                                    color: '#0d0d0d',
                                                    borderColor: '#f6cf02'
                                                }}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#f6cf02';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f6cf02';
                                                    e.currentTarget.style.color = '#0d0d0d';
                                                }}
                                            >
                                                Ver Catálogo
                                            </motion.button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                        </div>
                    </div>
                </div>

                {/* Additional Information Section */}
                <div className="py-16">
                    <div className="content-container">
                        <motion.div
                            className="max-w-4xl mx-auto text-center"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#0d0d0d' }}>
                                ¿Por qué elegir Jarad?
                            </h2>
                            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
                                Más de 30 años de experiencia en nutrición animal nos respaldan.
                                Nuestros productos están formulados con los más altos estándares de calidad.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <motion.div
                                    className="text-center"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <div
                                        className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                                        style={{ backgroundColor: '#f6cf02' }}
                                    >
                                        <svg className="w-8 h-8" style={{ color: '#0d0d0d' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold mb-2" style={{ color: '#0d0d0d' }}>Calidad Garantizada</h3>
                                    <p className="text-gray-600">Productos certificados con los más altos estándares de calidad</p>
                                </motion.div>

                                <motion.div
                                    className="text-center"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                    viewport={{ once: true }}
                                >
                                    <div
                                        className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                                        style={{ backgroundColor: '#f6cf02' }}
                                    >
                                        <svg className="w-8 h-8" style={{ color: '#0d0d0d' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold mb-2" style={{ color: '#0d0d0d' }}>Experiencia</h3>
                                    <p className="text-gray-600">Más de 30 años desarrollando nutrición especializada</p>
                                </motion.div>

                                <motion.div
                                    className="text-center"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                    viewport={{ once: true }}
                                >
                                    <div
                                        className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                                        style={{ backgroundColor: '#f6cf02' }}
                                    >
                                        <svg className="w-8 h-8" style={{ color: '#0d0d0d' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold mb-2" style={{ color: '#0d0d0d' }}>Compromiso</h3>
                                    <p className="text-gray-600">Dedicados al bienestar y salud de todas las mascotas</p>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="py-16 px-4 overflow-hidden" style={{ backgroundColor: '#0d0d0d' }}>
                    <div className="content-container text-center relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-white">
                                ¿Necesitas más información?
                            </h2>
                            <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90 font-light">
                                Nuestro equipo de expertos está disponible para asesorarte sobre
                                la mejor nutrición para tu mascota.
                            </p>
                            <motion.button
                                className="px-8 py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-all border-2"
                                style={{
                                    backgroundColor: '#f6cf02',
                                    color: '#0d0d0d',
                                    borderColor: '#f6cf02'
                                }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#f6cf02';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f6cf02';
                                    e.currentTarget.style.color = '#0d0d0d';
                                }}
                            >
                                Contactar con expertos
                            </motion.button>
                        </motion.div>

                        {/* Elementos decorativos animados */}
                        <motion.div
                            className="absolute top-0 right-0 w-32 h-32 rounded-full"
                            style={{ backgroundColor: '#f6cf02', opacity: 0.1 }}
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.1, 0.2, 0.1]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                        <motion.div
                            className="absolute bottom-0 left-0 w-24 h-24 rounded-full"
                            style={{ backgroundColor: '#f6cf02', opacity: 0.1 }}
                            animate={{
                                scale: [1, 1.3, 1],
                                opacity: [0.1, 0.15, 0.1]
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: 1
                            }}
                        />
                    </div>
                </div>
            </div>

        </>
    );
};

export default Catalog;